/**
 * HeaderBar - Condensed single-row header for Project Detail Page
 *
 * Displays: Back button, Project Name, Rush badge, WO#, PM, Sales, Artist, Due, Status
 * Actions: Opti indicator, Rework button, Start Timer button
 */

import { formatDateShort, isOverdue } from './projectUtils';

export default function HeaderBar({
  workOrder,
  hasOptiOperations,
  onBack,
  onStartTimer,
  onReworkClick,
}) {
  const wo = workOrder;

  return (
    <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
      <div className="px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Back button */}
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
            title="Back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          {/* Project Name + Rush Badge */}
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {wo.project_name || wo.projectName}
            </h1>
            {(wo.is_rush || wo.isRush) && (
              <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full flex items-center gap-1 flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                RUSH
              </span>
            )}
          </div>

          {/* Divider */}
          <div className="h-5 w-px bg-gray-200 flex-shrink-0" />

          {/* Project Info - simple text */}
          <div className="flex items-center gap-4 text-sm text-gray-600 flex-shrink-0">
            <span>
              <span className="font-medium text-gray-900">Q-{wo.quoteNumber || wo.quote_number || '—'}</span>
            </span>
            <span className="text-gray-300">|</span>
            <span>
              <span className="text-gray-500">PM:</span>{' '}
              <span className="text-gray-900">{wo.assigned_pm_name || wo.pm || '—'}</span>
            </span>
            <span>
              <span className="text-gray-500">Sales:</span>{' '}
              <span className="text-gray-900">{wo.sales_rep || wo.salesRep || '—'}</span>
            </span>
            <span>
              <span className="text-gray-500">Client:</span>{' '}
              <span className="text-gray-900">{wo.customerName || wo.customer_name || wo.clientName || '—'}</span>
            </span>
            <span className={`${isOverdue(wo.due_date || wo.dueDate) ? 'text-red-600' : ''}`}>
              <span className="text-gray-500">Due:</span>{' '}
              <span className={isOverdue(wo.due_date || wo.dueDate) ? 'text-red-600 font-medium' : 'text-gray-900'}>
                {formatDateShort(wo.due_date || wo.dueDate)}
              </span>
            </span>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Opti indicator */}
            {hasOptiOperations && (
              <span
                className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-md flex items-center gap-1"
                title="Contains Opti operations"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                OPTI
              </span>
            )}

            {/* Rework button */}
            <button
              onClick={onReworkClick}
              className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5"
              title="Report Rework"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Rework
            </button>

            {/* Start Timer button */}
            {onStartTimer && (
              <button
                onClick={() => onStartTimer(wo)}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-gray-900 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Start Timer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
